import { Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useGetBankingAuthExpiration } from "Api/Queries/Banks/useGetBankingAuthExpiration";
import { ExpiringAisBanksList } from "Components/Banking/Auth/ExpiringAisBanksList";
import { ExpiringPispBanksList } from "Components/Banking/Auth/ExpiringPispBanksList";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useCheckPermission } from "Hooks/usePermission";
import { Permission } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "Utils/Storage/SessionStorage/SecureSessionStorage";
import { SessionStorageKeys } from "Utils/Storage/SessionStorage/SessionStorageKeys";

const COMPONENT_RESOURCES = Resources.Components.RevalidateBankingAuthModal;
const SESSION_STORAGE_KEY = SessionStorageKeys.BankExpirationModalDismissed;

export const RevalidateBankingAuthModal = () => {
  const [switchToRerender, setSwitchToRerender] = useState<boolean>(false); //hackish way to rerender the component after updating session storage
  const { t } = useResource();
  const user = useLoggedInUser();
  const canReadBanking = useCheckPermission(
    AccessRightCodes.Banking,
    Permission.Read,
  );
  const isAdmin = user?.accessRoleCode === AccessRoleCodes.Admin;
  const { data, isLoading, isError } = useGetBankingAuthExpiration({
    enabled: canReadBanking && !isAdmin,
  });

  if (isLoading || isError) {
    return null;
  }

  if (!data) {
    return null;
  }

  // CODE ONLY FOR DEBUGGING PURPOSES - investigating bug e.map is not a function (SENTRY ISSUE MOONE-APP-REACT-CD) -------------------------
  if (
    !Array.isArray(data.expiringBanks) ||
    !Array.isArray(data.invalidBanks) ||
    !Array.isArray(data.expiringPispBanks) ||
    !Array.isArray(data.invalidPispBanks)
  ) {
    Sentry.captureMessage("Malformed banking auth expiration data", {
      level: "error",
      extra: {
        data,
        typeofData: typeof data,
        keys: data && typeof data === "object" ? Object.keys(data) : null,
        typeofExpiringBanks: typeof data?.expiringBanks,
        typeofInvalidBanks: typeof data?.invalidBanks,
        typeofExpiringPispBanks: typeof data?.expiringPispBanks,
        typeofInvalidPispBanks: typeof data?.invalidPispBanks,
        expiringBanksValue: data?.expiringBanks,
        invalidBanksValue: data?.invalidBanks,
        expiringPispBanksValue: data?.expiringPispBanks,
        invalidPispBanksValue: data?.invalidPispBanks,
      },
    });
    return null;
  }
  // ----------------------------------------------------------------------------------------------------------------------------------------

  const { expiringBanks, invalidBanks, expiringPispBanks, invalidPispBanks } =
    data;

  const hasExpiring = expiringBanks.length > 0;
  const hasInvalid = invalidBanks.length > 0;
  const hasExpiringPisp = expiringPispBanks.length > 0;
  const hasInvalidPisp = invalidPispBanks.length > 0;
  const hasAnyIssue =
    hasExpiring || hasInvalid || hasExpiringPisp || hasInvalidPisp;

  if (!hasAnyIssue) {
    return null;
  }

  const alreadyDismissedInCurrentSession =
    !!getSessionStorageItem(SESSION_STORAGE_KEY);

  if (alreadyDismissedInCurrentSession) {
    return null;
  }

  const rerenderComponent = () => setSwitchToRerender(!switchToRerender);
  const onDismiss = () => {
    setSessionStorageItem(SESSION_STORAGE_KEY, "true");
    rerenderComponent();
  };
  const hasInvalidBanks = hasInvalid || hasInvalidPisp;

  const title = hasInvalidBanks
    ? t(COMPONENT_RESOURCES.InvalidTitle)
    : t(COMPONENT_RESOURCES.Title);

  return (
    <ModalDialog isOpen onClose={onDismiss} title={title}>
      <Stack gap={3}>
        {hasInvalid && (
          <Stack gap={1}>
            <Typography>{t(COMPONENT_RESOURCES.InvalidDescription)}</Typography>
            <ExpiringAisBanksList environments={invalidBanks} />
          </Stack>
        )}
        {hasInvalidPisp && (
          <Stack gap={1}>
            <Typography>
              {t(COMPONENT_RESOURCES.InvalidPispDescription)}
            </Typography>
            <ExpiringPispBanksList environments={invalidPispBanks} />
          </Stack>
        )}
        {hasExpiring && (
          <Stack gap={1}>
            {hasInvalidBanks && (
              <Typography fontWeight="bold" mt={1}>
                {t(COMPONENT_RESOURCES.Title)}
              </Typography>
            )}
            <Typography>{t(COMPONENT_RESOURCES.Description)}</Typography>
            <ExpiringAisBanksList environments={expiringBanks} />
          </Stack>
        )}
        {hasExpiringPisp && (
          <Stack gap={1}>
            {(hasInvalidBanks || hasExpiring) && (
              <Typography fontWeight="bold" mt={1}>
                {t(COMPONENT_RESOURCES.ExpiringPispTitle)}
              </Typography>
            )}
            <Typography>
              {t(COMPONENT_RESOURCES.ExpiringPispDescription)}
            </Typography>
            <ExpiringPispBanksList environments={expiringPispBanks} />
          </Stack>
        )}
      </Stack>
    </ModalDialog>
  );
};
